/**
 * Icon layer — Hugeicons (free core set, stroke-rounded).
 *
 * Each export is a drop-in replacement for the Lucide component of the same
 * name: it accepts `className`, `size`, `strokeWidth`, `color` and any SVG
 * prop, and forwards refs. Import icons from `@/components/icons` everywhere.
 */
import * as React from "react";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";

import {
  ArrowDown01Icon as HgiArrowDown,
  ArrowLeft01Icon as HgiArrowLeft,
  ArrowRight01Icon as HgiArrowRight,
  ArrowUp01Icon as HgiArrowUp,
  ArrowUpRight01Icon as HgiArrowUpRight,
  BedIcon as HgiBed,
  Briefcase01Icon as HgiBriefcase,
  CalendarDaysIcon as HgiCalendarDays,
  Camera01Icon as HgiCamera,
  Cancel01Icon as HgiCancel,
  Car01Icon as HgiCar,
  CheckIcon as HgiCheck,
  ChevronDownIcon as HgiChevronDown,
  ChevronLeftIcon as HgiChevronLeft,
  ChevronRightIcon as HgiChevronRight,
  ChevronUpIcon as HgiChevronUp,
  CircleIcon as HgiCircle,
  ComputerIcon as HgiMonitor,
  CopyIcon as HgiCopy,
  CornerDownLeftIcon as HgiCornerDownLeft,
  Download01Icon as HgiDownload,
  GemIcon as HgiGem,
  GitBranchIcon as HgiGitBranch,
  GlobeIcon as HgiGlobe,
  GripVerticalIcon as HgiGripVertical,
  Image01Icon as HgiImage,
  InformationCircleIcon as HgiInfo,
  JusticeScale01Icon as HgiScale,
  LibraryIcon as HgiLibrary,
  LifebuoyIcon as HgiLifeBuoy,
  Link02Icon as HgiLink,
  Loading03Icon as HgiLoader,
  Message01Icon as HgiMessage,
  MinusSignIcon as HgiMinus,
  MoreHorizontalIcon as HgiMoreHorizontal,
  Package01Icon as HgiPackage,
  PanelLeftIcon as HgiPanelLeft,
  PenTool01Icon as HgiPenTool,
  PlusSignIcon as HgiPlus,
  RepeatIcon as HgiRepeat,
  ScissorIcon as HgiScissors,
  Search01Icon as HgiSearch,
  ServerIcon as HgiServer,
  Shield01Icon as HgiShield,
  SparklesIcon as HgiSparkles,
  Tag01Icon as HgiTag,
  UserIcon as HgiUser,
  ZapIcon as HgiZap,
  CallIcon as HgiCall,
  CopyIcon as HgiCopyIcon,
  DiscordIcon as HgiDiscord,
  AddressBookIcon as HgiAddressBook,
  LinkedinIcon as HgiLinkedin,
  MailIcon as HgiMail,
  QrCodeIcon as HgiQrCode,
  TwitterIcon as HgiTwitter,
  UserPlusIcon as HgiUserPlus,
  SquareIcon as HgiSquare,
  FlipHorizontalIcon as HgiFlipHorizontal,
} from "@hugeicons/core-free-icons";

export type IconProps = Omit<React.SVGProps<SVGSVGElement>, "strokeWidth"> & {
  size?: string | number;
  strokeWidth?: number;
  absoluteStrokeWidth?: boolean;
};

/** Compatibility alias for code that typed icon props as `LucideIcon`. */
export type LucideIcon = React.ForwardRefExoticComponent<
  IconProps & React.RefAttributes<SVGSVGElement>
>;
export type IconComponent = LucideIcon;

function createIcon(displayName: string, icon: IconSvgElement): LucideIcon {
  const Component = React.forwardRef<SVGSVGElement, IconProps>(
    ({ strokeWidth = 1.8, size, ...props }, ref) => (
      <HugeiconsIcon
        ref={ref}
        icon={icon}
        strokeWidth={strokeWidth}
        {...(size !== undefined ? { size } : {})}
        {...props}
      />
    ),
  );
  Component.displayName = displayName;
  return Component;
}

export const ArrowDown = createIcon("ArrowDown", HgiArrowDown);
export const ArrowDownIcon = ArrowDown;
export const ArrowLeft = createIcon("ArrowLeft", HgiArrowLeft);
export const ArrowLeftIcon = ArrowLeft;
export const ArrowRight = createIcon("ArrowRight", HgiArrowRight);
export const ArrowRightIcon = ArrowRight;
export const ArrowUp = createIcon("ArrowUp", HgiArrowUp);
export const ArrowUpIcon = ArrowUp;
export const ArrowUpRight = createIcon("ArrowUpRight", HgiArrowUpRight);
export const ArrowUpRightIcon = ArrowUpRight;
export const Bed = createIcon("Bed", HgiBed);
export const Briefcase = createIcon("Briefcase", HgiBriefcase);
export const CalendarDays = createIcon("CalendarDays", HgiCalendarDays);
export const Camera = createIcon("Camera", HgiCamera);
export const Car = createIcon("Car", HgiCar);
export const Check = createIcon("Check", HgiCheck);
export const CheckIcon = Check;
export const ChevronDown = createIcon("ChevronDown", HgiChevronDown);
export const ChevronDownIcon = ChevronDown;
export const ChevronLeft = createIcon("ChevronLeft", HgiChevronLeft);
export const ChevronLeftIcon = ChevronLeft;
export const ChevronRight = createIcon("ChevronRight", HgiChevronRight);
export const ChevronRightIcon = ChevronRight;
export const ChevronUp = createIcon("ChevronUp", HgiChevronUp);
export const ChevronUpIcon = ChevronUp;
export const Circle = createIcon("Circle", HgiCircle);
export const CircleIcon = Circle;
export const Copy = createIcon("Copy", HgiCopy);
export const CopyIcon = Copy;
export const CornerDownLeft = createIcon("CornerDownLeft", HgiCornerDownLeft);
export const CornerDownLeftIcon = CornerDownLeft;
export const Download = createIcon("Download", HgiDownload);
export const DownloadIcon = Download;
export const Gem = createIcon("Gem", HgiGem);
export const GitBranch = createIcon("GitBranch", HgiGitBranch);
export const Globe = createIcon("Globe", HgiGlobe);
export const GripVertical = createIcon("GripVertical", HgiGripVertical);
export const Image = createIcon("Image", HgiImage);
export const ImageIcon = Image;
export const Info = createIcon("Info", HgiInfo);
export const InfoIcon = Info;
export const LibraryBig = createIcon("LibraryBig", HgiLibrary);
export const LifeBuoy = createIcon("LifeBuoy", HgiLifeBuoy);
export const Link2 = createIcon("Link2", HgiLink);
export const Loader2 = createIcon("Loader2", HgiLoader);
export const Loader2Icon = Loader2;
export const MessageSquare = createIcon("MessageSquare", HgiMessage);
export const Minus = createIcon("Minus", HgiMinus);
export const MinusIcon = Minus;
export const Monitor = createIcon("Monitor", HgiMonitor);
export const MoreHorizontal = createIcon("MoreHorizontal", HgiMoreHorizontal);
export const MoreHorizontalIcon = MoreHorizontal;
export const Package = createIcon("Package", HgiPackage);
export const PanelLeft = createIcon("PanelLeft", HgiPanelLeft);
export const PenTool = createIcon("PenTool", HgiPenTool);
export const Plus = createIcon("Plus", HgiPlus);
export const PlusIcon = Plus;
export const Repeat = createIcon("Repeat", HgiRepeat);
export const Scale = createIcon("Scale", HgiScale);
export const Scissors = createIcon("Scissors", HgiScissors);
export const Search = createIcon("Search", HgiSearch);
export const SearchIcon = Search;
export const Server = createIcon("Server", HgiServer);
export const Shield = createIcon("Shield", HgiShield);
export const Sparkles = createIcon("Sparkles", HgiSparkles);
export const Square = createIcon("Square", HgiSquare);
export const SquareIcon = Square;
export const Tag = createIcon("Tag", HgiTag);
export const User = createIcon("User", HgiUser);
export const X = createIcon("X", HgiCancel);
export const XIcon = X;
export const Copy01 = createIcon("Copy01", HgiCopyIcon);
export const Copy01Icon = Copy01;
export const Call = createIcon("Call", HgiCall);
export const Mail = createIcon("Mail", HgiMail);
export const QrCode = createIcon("QrCode", HgiQrCode);
export const AddressBook = createIcon("AddressBook", HgiAddressBook);
export const Linkedin = createIcon("Linkedin", HgiLinkedin);
export const Twitter = createIcon("Twitter", HgiTwitter);
export const Discord = createIcon("Discord", HgiDiscord);
export const UserPlus = createIcon("UserPlus", HgiUserPlus);
export const Zap = createIcon("Zap", HgiZap);
export const FlipHorizontal = createIcon("FlipHorizontal", HgiFlipHorizontal);

/* Custom solid icons (from the Hugeicons AI pack, supplied as raw SVGs). */
const createSolidIcon = (displayName: string, children: React.ReactNode) => {
  const Component = React.forwardRef<SVGSVGElement, IconProps>(
    ({ size, ...props }, ref) => (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        color="currentColor"
        {...(size !== undefined ? { width: size, height: size } : {})}
        {...props}
      >
        {children}
      </svg>
    ),
  );
  Component.displayName = displayName;
  return Component;
};

export const CallSolid = createSolidIcon(
  "CallSolid",
  <path d="M12 1.25C13.6287 1.25 14.9257 1.24834 15.9404 1.38477C16.9767 1.52409 17.8298 1.81999 18.5049 2.49512C19.18 3.17025 19.4759 4.02332 19.6152 5.05957C19.7517 6.07426 19.75 7.37129 19.75 9V15C19.75 16.6287 19.7517 17.9257 19.6152 18.9404C19.4759 19.9767 19.18 20.8298 18.5049 21.5049C17.8298 22.18 16.9767 22.4759 15.9404 22.6152C14.9257 22.7517 13.6287 22.75 12 22.75C10.3713 22.75 9.07426 22.7517 8.05957 22.6152C7.02332 22.4759 6.17025 22.18 5.49512 21.5049C4.81999 20.8298 4.52409 19.9767 4.38477 18.9404C4.24834 17.9257 4.25 16.6287 4.25 15V9C4.25 7.37129 4.24834 6.07426 4.38477 5.05957C4.52409 4.02332 4.81998 3.17025 5.49512 2.49512C6.17025 1.81998 7.02332 1.52409 8.05957 1.38477C9.07426 1.24834 10.3713 1.25 12 1.25ZM12 2.75C10.3291 2.75 9.15123 2.75128 8.25977 2.87109C7.38983 2.98805 6.90565 3.20568 6.55566 3.55566C6.20568 3.90565 5.98805 4.38983 5.87109 5.25977C5.75128 6.15123 5.75 7.32908 5.75 9V15C5.75 16.6709 5.75128 17.8488 5.87109 18.7402C5.98805 19.6102 6.20568 20.0943 6.55566 20.4443C6.90565 20.7943 7.38983 21.0119 8.25977 21.1289C9.15123 21.2487 10.3291 21.25 12 21.25C13.6709 21.25 14.8488 21.2487 15.7402 21.1289C16.6102 21.0119 17.0943 20.7943 17.4443 20.4443C17.7943 20.0943 18.0119 19.6102 18.1289 18.7402C18.2487 17.8488 18.25 16.6709 18.25 15V9C18.25 7.32909 18.2487 6.15124 18.1289 5.25977C18.0119 4.38983 17.7943 3.90565 17.4443 3.55566C17.0943 3.20568 16.6102 2.98805 15.7402 2.87109C14.8488 2.75128 13.6709 2.75 12 2.75ZM12 17.75C12.5523 17.75 13 18.1977 13 18.75C13 19.3023 12.5523 19.75 12 19.75C11.4477 19.75 11 19.3023 11 18.75C11 18.1977 11.4477 17.75 12 17.75ZM11.2822 6.72559C11.5345 6.09154 12.4655 6.09156 12.7178 6.72559L12.7607 6.87109L12.8145 7.0918C13.122 8.17735 14.0112 9.01082 15.1289 9.23926C15.9569 9.40877 15.9569 10.5912 15.1289 10.7607C13.9367 11.0044 13.0044 11.9367 12.7607 13.1289C12.5912 13.9569 11.4088 13.9569 11.2393 13.1289C10.9956 11.9367 10.0633 11.0044 8.87109 10.7607C8.04305 10.5912 8.04308 9.40876 8.87109 9.23926L9.0918 9.18555C10.1774 8.87803 11.0108 7.98875 11.2393 6.87109L11.2822 6.72559ZM12 8.83203C11.6853 9.28959 11.2896 9.6853 10.832 10C11.2894 10.3146 11.6854 10.7097 12 11.167C12.3145 10.7099 12.7099 10.3145 13.167 10C12.7097 9.68538 12.3146 9.28937 12 8.83203Z" />,
);
export const CallSolidIcon = CallSolid;

export const WebSolid = createSolidIcon(
  "WebSolid",
  <>
    <path d="M11.4224 7.40215C11.6208 6.86595 12.3792 6.86595 12.5776 7.40215L13.4669 9.80538C13.5916 10.1425 13.8575 10.4084 14.1946 10.5331L16.5979 11.4224C17.134 11.6208 17.1341 12.3792 16.5979 12.5776L14.1946 13.4669C13.8575 13.5916 13.5916 13.8575 13.4669 14.1946L12.5776 16.5979C12.3792 17.134 11.6208 17.1341 11.4224 16.5979L10.5331 14.1946C10.4084 13.8575 10.1425 13.5916 9.80538 13.4669L7.40215 12.5776C6.86595 12.3792 6.86595 11.6208 7.40215 11.4224L9.80538 10.5331C10.1425 10.4084 10.4084 10.1425 10.5331 9.80538L11.4224 7.40215Z" />
    <path fillRule="evenodd" clipRule="evenodd" d="M2.77997 11.25H4.5C4.91421 11.25 5.25 11.5858 5.25 12C5.25 12.4142 4.91421 12.75 4.5 12.75H2.77997C3.07787 16.4628 5.56818 19.557 8.95538 20.7372C8.95487 20.7365 8.95436 20.7357 8.95385 20.7349C8.19137 19.5689 7.61154 17.9674 7.26327 16.1404C7.1857 15.7336 7.45267 15.3408 7.85956 15.2633C8.26644 15.1857 8.65917 15.4527 8.73673 15.8596C9.06173 17.5644 9.58734 18.9629 10.2093 19.914C10.8446 20.8855 11.4782 21.25 12 21.25C12.5218 21.25 13.1554 20.8855 13.7907 19.914C14.4127 18.9629 14.9383 17.5644 15.2633 15.8596C15.3408 15.4527 15.7336 15.1857 16.1404 15.2633C16.5473 15.3408 16.8143 15.7336 16.7367 16.1404C16.3885 17.9674 15.8086 19.5689 15.0462 20.7349C15.0456 20.7357 15.0451 20.7365 15.0446 20.7372C18.4318 19.557 20.9221 16.4628 21.22 12.75H19.5C19.0858 12.75 18.75 12.4142 18.75 12C18.75 11.5858 19.0858 11.25 19.5 11.25H21.22C20.9221 7.5372 18.4318 4.44295 15.0446 3.26275C15.0451 3.26353 15.0456 3.26432 15.0462 3.2651C15.8086 4.4311 16.3885 6.03261 16.7367 7.85956C16.8143 8.26644 16.5473 8.65917 16.1404 8.73673C15.7336 8.8143 15.3408 8.54733 15.2633 8.14044C14.9383 6.43558 14.4127 5.03709 13.7907 4.08605C13.1554 3.11454 12.5218 2.75 12 2.75C11.4782 2.75 10.8446 3.11454 10.2093 4.08605C9.58734 5.03709 9.06173 6.43558 8.73673 8.14044C8.65917 8.54733 8.26644 8.8143 7.85956 8.73673C7.45267 8.65917 7.1857 8.26644 7.26327 7.85956C7.61154 6.03261 8.19137 4.4311 8.95385 3.2651C8.95436 3.26432 8.95487 3.26354 8.95538 3.26276C5.56818 4.44295 3.07787 7.53721 2.77997 11.25ZM12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25Z" />
  </>,
);
export const WebSolidIcon = WebSolid;

export const MailSolid = createSolidIcon(
  "MailSolid",
  <path d="M14.92,2.787 L14.978,2.788 C16.503,2.827 17.73,2.857 18.713,3.029 C19.743,3.208 20.58,3.552 21.286,4.261 C21.99,4.968 22.332,5.793 22.508,6.805 C22.676,7.77 22.701,8.967 22.733,10.45 L22.734,10.508 C22.755,11.505 22.755,12.495 22.734,13.492 L22.733,13.55 C22.701,15.033 22.676,16.23 22.508,17.195 C22.332,18.207 21.99,19.032 21.286,19.739 C20.58,20.448 19.743,20.792 18.713,20.971 C17.73,21.143 16.503,21.174 14.978,21.212 L14.92,21.213 C12.967,21.262 11.033,21.262 9.08,21.213 L9.022,21.212 C7.497,21.174 6.27,21.143 5.287,20.971 C4.257,20.792 3.42,20.448 2.714,19.739 C2.01,19.032 1.668,18.207 1.492,17.195 C1.324,16.23 1.299,15.033 1.267,13.55 L1.266,13.492 C1.245,12.495 1.245,11.505 1.266,10.508 L1.267,10.45 L1.267,10.45 C1.299,8.967 1.324,7.77 1.492,6.805 C1.668,5.793 2.01,4.968 2.714,4.261 C3.42,3.552 4.257,3.208 5.287,3.029 C6.27,2.857 7.497,2.827 9.022,2.788 L9.08,2.787 C11.033,2.738 12.967,2.738 14.92,2.787 Z M2.921,7.38 C2.818,8.173 2.795,9.174 2.766,10.54 C2.745,11.515 2.745,12.485 2.766,13.46 C2.799,15.015 2.824,16.098 2.97,16.938 C3.109,17.742 3.349,18.251 3.776,18.68 C4.201,19.106 4.717,19.35 5.544,19.494 C6.405,19.644 7.521,19.674 9.118,19.714 C11.046,19.762 12.954,19.762 14.882,19.714 C16.479,19.674 17.595,19.644 18.456,19.494 C19.284,19.35 19.799,19.106 20.224,18.68 C20.651,18.251 20.891,17.742 21.03,16.938 C21.176,16.098 21.201,15.015 21.234,13.46 C21.255,12.485 21.255,11.515 21.234,10.54 C21.205,9.175 21.182,8.173 21.079,7.381 L15.457,10.566 C14.164,11.299 13.113,11.746 12.001,11.746 C10.888,11.746 9.837,11.299 8.544,10.566 Z M9.118,4.286 C7.521,4.326 6.405,4.356 5.544,4.506 C4.717,4.65 4.201,4.894 3.776,5.32 C3.603,5.494 3.461,5.681 3.343,5.895 L9.283,9.261 C10.539,9.972 11.3,10.246 12.001,10.246 C12.701,10.246 13.462,9.972 14.718,9.261 L20.657,5.896 C20.539,5.681 20.397,5.494 20.224,5.32 C19.799,4.894 19.284,4.65 18.456,4.506 C17.595,4.356 16.479,4.326 14.882,4.286 C12.954,4.238 11.046,4.238 9.118,4.286 Z" />,
);
export const MailSolidIcon = MailSolid;

export const LinkedinSolid = createSolidIcon(
  "LinkedinSolid",
  <path d="M22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0zM7.433 20.452H3.555V9h3.878v11.452zM5.494 7.433a2.25 2.25 0 1 1 0-4.5 2.25 2.25 0 0 1 0 4.5zm15.77 13.019h-3.877v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.522V9h3.723v1.561h.052c.519-.982 1.787-2.018 3.678-2.018 3.935 0 4.663 2.589 4.663 5.954v5.955z" />,
);
export const LinkedinSolidIcon = LinkedinSolid;
